import { FormGroup } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import React from "react";
import { bindActionCreators } from "redux";

import PagePathField from "common/form/fields/PagePathField";
import CheckBoxWidget from "common/form/widgets/CheckBoxWidget";
import TextWidget from "common/form/widgets/TextWidget";
import Icon from "common/views/icon/Icon";
import * as paramsActions from "core/actions/paramsActions";
import Label from "core/components/label/Label";
import PanelTitle from "core/components/label/PanelTitle";
import { lastItem } from "core/util/ArrayUtil";
import { IBreadcrumb } from "core/util/IBreadcrumb";
import PanelUtil from "core/util/PanelUtil";
import UrlUtil from "core/util/UrlUtil";
import { ILinkType, IStaticLinkData, LinkTypeRegistry } from "../ILinkType";

/**
 * nav link to panel instance path.
 *
 * @author chills
 */

interface IPageLink extends IStaticLinkData {
	pagePath: string[];
	preserveParams?: boolean;
	preserveBreadcrumbs?: boolean;
}

const pageLink: ILinkType<IPageLink> = {
	type: "page",
	label: "Page Link",
	icon: IconNames.DOCUMENT,
	renderInput: (value, onChange: (value: IPageLink) => void) => (
		<FormGroup>
			<FormGroup label="Target Page">
				<PagePathField
					onChange={(pagePath) => onChange({...value, pagePath})}
					formData={value.pagePath}
					uiSchema={undefined}
					idSchema={undefined}
				/>
			</FormGroup>
			<FormGroup label="Preserve Page Params">
				<CheckBoxWidget onChange={(preserveParams) => onChange({...value, preserveParams})} value={value.preserveParams} />
			</FormGroup>
			<FormGroup label="Preserve Breadcrumbs">
				<CheckBoxWidget onChange={(preserveBreadcrumbs) => onChange({...value, preserveBreadcrumbs})} value={value.preserveBreadcrumbs} />
			</FormGroup>
			<FormGroup label="Override Page Label">
				<TextWidget value={value.label} onChange={(label) => onChange({...value, label})} />
			</FormGroup>
			<FormGroup label="Override Icon">
				<TextWidget value={value.icon} onChange={(icon) => onChange({...value, icon})} />
			</FormGroup>
		</FormGroup>
	),
	mapDataToMenuNode: (data, {id, children, isExpanded}, state) => {

		const panelInstance = data.pagePath && lastItem(data.pagePath);

		const panel = state.config.panels[panelInstance];

		return {
			id,
			children,
			data,
			label: !!data.label ? <Label k={'link.' + id} v={data.label} /> : (panelInstance ? <PanelTitle instance={panelInstance} /> : "No Page Selected"),
			icon: !!data.icon ? <Icon icon={data.icon} /> : (panel && panel.icon || null),
			isActive: UrlUtil.isPagePathActive(state, data.pagePath),
			isExpanded,
			isVisible: PanelUtil.isAccessible(panel, state),
			attrs: {
				'data-wap_ref': panel.label
			}
		};
	},
	doNavigate: (data, dispatch, id) => {
		if (data.pagePath && data.pagePath.length) {
			dispatch((disp, getState) => {

				const state = getState();
				const instance = lastItem(data.pagePath);
				const panel = state.config.panels[instance];

				if (panel) {
					// get configured path for selected panel path
					const pathname = UrlUtil.getPagePathLink(state, data.pagePath);

					// create key for breadcrumb based on selected page path
					const key = 'page:' + pathname;

					const linkCrumb: IBreadcrumb = {
						key,
						label: !!data.label ? { k: 'link.' + id, v: data.label } : { k: `panel.${instance}.title`, v: panel.title },
						target: {
							pathname,
							search: data.preserveParams ? (state as any).router.location.search : ''
						}
					};

					// build next crumbs list
					let crumbs: IBreadcrumb[];
					if (data.preserveBreadcrumbs) {
						crumbs = [
							// filter out any crumbs with the same key???
							...(state.params.breadcrumbs || []).filter((c) => c.key !== key),
							linkCrumb
						];
					} else {
						// replace crumbs
						crumbs = [linkCrumb];
					}

					disp(paramsActions.pushPanelPath(data.pagePath, data.preserveParams, crumbs));
				}
			});
		}
	}
};

export default LinkTypeRegistry.registerType(pageLink);



// WEBPACK FOOTER //
// ./src/common/views/menuTree/linkTypes/PageLink.tsx